import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const ViewPhotosModal = ({ milestone, open, onClose, onSuccess, userRole }) => {
  const canApprove = userRole === 'customer' && milestone?.admin_approved && !milestone?.customer_approved;
  const canAdminApprove = userRole === 'admin' && milestone?.engineer_approved && !milestone?.admin_approved;

  const handleApprove = async () => {
    try {
      await api.post(`/milestones/${milestone.milestone_id}/approve`);
      
      if (userRole === 'admin') {
        toast.success('Milestone approved. Customer can now review.');
      } else {
        toast.success('Milestone approved and marked as complete!');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve milestone');
    }
  };

  const getApprovalStatus = () => {
    if (!milestone) return null;
    
    if (milestone.customer_approved) {
      return <Badge className="bg-green-100 text-green-700">Fully Approved</Badge>;
    } else if (milestone.admin_approved) {
      return <Badge className="bg-blue-100 text-blue-700">Admin Approved - Awaiting Customer</Badge>;
    } else if (milestone.engineer_approved) {
      return <Badge className="bg-yellow-100 text-yellow-700">Under Review - Awaiting Admin</Badge>;
    }
    return <Badge className="bg-stone-200 text-stone-700">Not Submitted</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{milestone?.stage_name}</DialogTitle>
          <DialogDescription>
            {milestone?.description}
          </DialogDescription>
          <div className="mt-2">
            {getApprovalStatus()}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {milestone?.completion_photos && milestone.completion_photos.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                {milestone.completion_photos.map((photo, idx) => (
                  <div key={idx} className="aspect-video bg-stone-200 rounded-sm flex items-center justify-center">
                    <ImageIcon className="h-12 w-12 text-stone-400" />
                    <p className="text-xs text-stone-500 ml-2">Photo {idx + 1}</p>
                  </div>
                ))}
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-sm p-4">
                <h4 className="font-semibold text-sm text-stone-950 mb-2">Completion Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-stone-600">Expected End</p>
                    <p className="font-medium">{new Date(milestone.expected_end).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-stone-600">Actual End</p>
                    <p className="font-medium">
                      {milestone.actual_end ? new Date(milestone.actual_end).toLocaleDateString('en-IN') : 'In Progress'}
                    </p>
                  </div>
                </div>
              </div>

              {(canApprove || canAdminApprove) && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                  <p className="text-sm text-blue-900 mb-3">
                    {canAdminApprove 
                      ? 'Please review the photos and approve if the work meets quality standards.'
                      : 'Admin has reviewed this work. Please approve to mark as complete.'}
                  </p>
                  <Button
                    onClick={handleApprove}
                    className="w-full bg-green-700 hover:bg-green-800 text-white"
                    data-testid={`approve-milestone-${milestone.milestone_id}`}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Approve Completion
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <ImageIcon className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No photos uploaded yet</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewPhotosModal;
